import { apiFetch } from "./client";
import type { ListResponse } from "./types";

// The largest page every list endpoint accepts.
const PAGE_SIZE = 100;
// Backstop against a cursor that never runs out: 5,000 items.
const MAX_PAGES = 50;

// fetchAllPages follows a list endpoint's cursor until it runs out and
// returns every item as one list.
//
// Only for collections that are bounded by design (tier caps or catalogue
// size) — companies, a tenant's groups, users, device types/models, API
// credentials, group keys — that a page needs whole: lookup maps, picker
// options, or a table searched in the browser. Past the first page an item
// would otherwise render as "Unknown", be unselectable, or be missing from
// the search. Never for devices, tasks, audit or other collections that grow
// with fleet size: those are paged, filtered and counted on the server.
export async function fetchAllPages<T>(path: string, init: RequestInit = {}): Promise<ListResponse<T>> {
  const data: T[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const url = new URL(path, "http://placeholder");
    url.searchParams.set("limit", String(PAGE_SIZE));
    if (cursor) url.searchParams.set("cursor", cursor);

    const res = await apiFetch<Partial<ListResponse<T>>>(`${url.pathname}${url.search}`, init);
    data.push(...(res.data ?? []));
    // A response without the pagination envelope is a complete list.
    if (!res.pagination?.hasMore || !res.pagination.cursor) {
      return { data, pagination: { count: data.length, hasMore: false } };
    }
    cursor = res.pagination.cursor;
  }

  // Hit the backstop: return what we have and say there is more, rather
  // than spinning or pretending the list is complete.
  return { data, pagination: { count: data.length, hasMore: true, cursor } };
}

// wantsAllPages: a list hook called without paging params serves its whole
// (bounded) collection; pass limit/cursor to drive a pager instead.
export function wantsAllPages(params: { limit?: number; cursor?: string }): boolean {
  return !params.limit && !params.cursor;
}
